import { findLastIndex, min } from 'lodash';
import { useCallback, useEffect, useMemo, useState, MouseEvent as ReactMouseEvent } from 'react';

const zoomLevels = [0.3, 0.5, 0.67, 0.8, 0.9, 1, 1.1, 1.2, 1.33, 1.5, 1.7, 2, 2.4, 3, 4, 5];
const minZoomLevel = 0.1;
const maxZoomLevel = 5;

const getNewZoom = (currentZoom: number | null, direction: '+' | '-'): number | null => {
  if (currentZoom === null) return null;
  // checking if current zoom is among presets
  const currentZoomIndex = zoomLevels.findIndex((x) => x === currentZoom);
  if (currentZoomIndex !== -1) {
    const newZoomIndex = currentZoomIndex + (direction === '+' ? 1 : -1);
    if (newZoomIndex < 0 || newZoomIndex >= zoomLevels.length) return zoomLevels[currentZoomIndex];
    return zoomLevels[newZoomIndex];
  } else {
    const newZoomIndex =
      direction === '+'
        ? zoomLevels.findIndex((x) => x > currentZoom)
        : findLastIndex(zoomLevels, (x) => x < currentZoom);
    if (newZoomIndex === -1) {
      return direction === '+' ? maxZoomLevel : minZoomLevel;
    }
    return zoomLevels[newZoomIndex];
  }
};

export const useImageControls = (
  enabled: boolean,
  containerRef: React.RefObject<HTMLElement>,
  attachEventsToDocument: boolean,
  realDimensions?: { width: number; height: number },
) => {
  const [zoomLevel, setZoomLevel] = useState<number | null>(null);
  const [dx, setDx] = useState(0);
  const [dy, setDy] = useState(0);
  const [rot, setRotate] = useState(0);
  const [panning, setPanning] = useState(false);
  const cssTransform = `translate(${dx}px, ${dy}px) scale(${zoomLevel ?? 0}) rotate(${rot}turn)`;
  const drag = () => setPanning(true);
  const dragOnMouseDown = (e: ReactMouseEvent) => {
    if (e.button === 0) drag();
  };

  const zoomIn = () => setZoomLevel((zoomLevel) => getNewZoom(zoomLevel, '+'));
  const zoomOut = () => setZoomLevel((zoomLevel) => getNewZoom(zoomLevel, '-'));
  const rotateLeft = () => setRotate((r) => r - 0.25);
  const rotateRight = () => setRotate((r) => r + 0.25);

  const resetPosition = useCallback(() => {
    setDx(0);
    setDy(0);
    setRotate(0);
    setPanning(false);
  }, []);

  /**
   * @param fit
   */

  const realWidth = realDimensions?.width;
  const realHeight = realDimensions?.height;
  const fit = useMemo(() => {
    const cntr = containerRef.current;
    if (cntr === null || realHeight === undefined || realWidth === undefined) return null;
    return () => {
      const zlHeight = cntr.clientHeight / realHeight;
      const zlWidth = cntr.clientWidth / realWidth;
      const chosen = min([min([zlHeight, zlWidth]), 1]);
      setZoomLevel(chosen ?? 1);
    };
  }, [containerRef, realWidth, realHeight]);

  const reset = useCallback(
    (resetZoom: 'original' | 'fit-if-possible') => {
      resetPosition();
      if (resetZoom === 'fit-if-possible') {
        if (fit) {
          fit();
        }
        // Если fit ещё недоступен, не меняем zoomLevel.
        // Это позволяет дождаться первого корректного значения без промежуточного моргания.
        return;
      }
      setZoomLevel(1);
    },
    [resetPosition, fit],
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!enabled) return;
      const { movementX, movementY } = e;
      if (panning) {
        setDx((dx) => dx + movementX);
        setDy((dy) => dy + movementY);
      }
    };
    const onMouseUp = () => {
      setPanning(false);
    };

    const onWheel = (e: WheelEvent) => {
      if (!enabled) return;
      const wheelInsideContainer =
        containerRef.current !== null && containerRef.current.contains(e.target as HTMLElement);
      if (attachEventsToDocument || wheelInsideContainer) {
        e.preventDefault();
        const dZoom = -e.deltaY;
        setZoomLevel((zl) => {
          if (zl === null) return null;
          const newZoomLevel = zl * Math.pow(1.02, dZoom);
          if (newZoomLevel > maxZoomLevel) return maxZoomLevel;
          if (newZoomLevel < minZoomLevel) return minZoomLevel;
          return newZoomLevel;
        });
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#passive
    // otherwise browser will ignore preventDefault() because `wheel` is passive by default
    document.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [panning, attachEventsToDocument, enabled, containerRef]);

  useEffect(() => {
    reset('fit-if-possible');
  }, [reset]);

  return {
    zoomLevel,
    zoomLevelString: zoomLevel !== null ? `${Math.round(zoomLevel * 100)}%` : '',
    cssTransform,
    drag,
    dragOnMouseDown,
    zoomIn,
    zoomOut,
    rotateLeft,
    rotateRight,
    reset,
    canZoomToFit: fit !== null,
  };
};
